import EmailIcon from '@mui/icons-material/Drafts';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { MemberEmailForm, ProgressButton } from 'components';
import { useCallback } from 'react';
import ModalTestEmail from 'routes/dashboard/components/MemberNotifications/ModalTestEmail';
import {
	useCreateEmailCarbonCopyMutation,
	useDeleteEmailCarbonCopyMutation,
	useEditMemberNotificationMutation,
	useGetMemberNotificationByIdQuery
} from 'store/api/version-api.slice';

import { getCarbonCopiesForCreate, getCarbonCopiesForDeletion, isMissingData } from '../helpers';
import { styles } from '../styles/emailsList.styles';

const EditNotificationEmailDialog = ({
	editEmailFormValues,
	editDialogOpen,
	handleCloseEditDialog,
	handleEditFormValueChange,
	handleToggleTestEmail,
	toggleTestEmail
}) => {
	const [editMemberNotification, { isLoading: isEditingNotification }] = useEditMemberNotificationMutation();
	const [createEmailCC, { isLoading: isCreateLoading }] = useCreateEmailCarbonCopyMutation();
	const [deleteEmailCC, { isLoading: isDeleteLoading }] = useDeleteEmailCarbonCopyMutation();

	const { data: currentEmailNotification = {}, isLoading: isGetNotificationLoading } =
		useGetMemberNotificationByIdQuery(editEmailFormValues.id, { skip: !editEmailFormValues.id });

	const handlePatchEmail = useCallback(async () => {
		const previousIds = currentEmailNotification.memberemailformcc_set.map(cc => cc.id);
		const currentIds = editEmailFormValues.memberemailformcc_set.map(cc => cc.id);

		const carbonCopiesIdsToDelete = getCarbonCopiesForDeletion({
			previousCCIds: previousIds,
			currentCCIds: currentIds
		});

		const carbonCopiesToCreate = getCarbonCopiesForCreate(editEmailFormValues.memberemailformcc_set);

		// if email has an id than we can fire update carbon copy requests in parallel
		const editPromise = editMemberNotification({ id: editEmailFormValues.id, payload: editEmailFormValues });

		const deleteCCPromises = carbonCopiesIdsToDelete.map(id => deleteEmailCC(id));
		const createCCPromises = carbonCopiesToCreate.map(cc => createEmailCC(cc));

		await Promise.allSettled([...deleteCCPromises, ...createCCPromises, editPromise]);

		handleCloseEditDialog();
	}, [
		createEmailCC,
		currentEmailNotification.memberemailformcc_set,
		deleteEmailCC,
		editEmailFormValues,
		editMemberNotification,
		handleCloseEditDialog
	]);

	const isSaveLoading = isEditingNotification || isCreateLoading || isDeleteLoading;
	const isSaveDisabled =
		isGetNotificationLoading ||
		isEditingNotification ||
		isCreateLoading ||
		isDeleteLoading ||
		isMissingData(editEmailFormValues);

	return (
		<Dialog
			open={editDialogOpen}
			onClose={handleCloseEditDialog}
			maxWidth={'md'}
			fullWidth={true}
			/*
					Weird hack to make TinyMCE code editor work in material Dialog component.
					Without resetting tabIndex => user can't modify anything in code editor panel
				*/
			TransitionProps={{ tabIndex: '' }}
		>
			<DialogTitle>Edit Notification Email</DialogTitle>
			<DialogContent>
				<MemberEmailForm
					id={editEmailFormValues.id}
					email_type={editEmailFormValues.email_type}
					email_body={editEmailFormValues.email_body}
					frequency={editEmailFormValues.frequency}
					email_subject={editEmailFormValues.email_subject}
					memberemailformcc_set={editEmailFormValues.memberemailformcc_set}
					handleChange={handleEditFormValueChange}
				/>
			</DialogContent>
			<DialogActions>
				<Button
					sx={styles.btnTestEmail}
					onClick={handleToggleTestEmail}
					color="primary"
					disabled={isMissingData(editEmailFormValues)}
				>
					<EmailIcon sx={styles.iconOffset} /> Test Email
				</Button>
				<Button sx={styles.actionButtons} onClick={handleCloseEditDialog} color="secondary">
					Cancel
				</Button>

				<ProgressButton
					isLoading={isSaveLoading}
					sx={{ flexGrow: 0 }}
					button={
						<Button
							sx={{ ...styles.actionButtons, ...styles.saveBtn }}
							onClick={handlePatchEmail}
							variant="contained"
							color="primary"
							disabled={isSaveDisabled}
						>
							<SaveIcon sx={styles.iconOffset} />
							Save
						</Button>
					}
					circleColor={'secondary'}
				/>
			</DialogActions>
			{toggleTestEmail && <ModalTestEmail handleClose={handleToggleTestEmail} data={editEmailFormValues} />}
		</Dialog>
	);
};

export default EditNotificationEmailDialog;
